import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { Promotion } from "@omni/types/api/product/bff/shared/promotion.model";
import { SalesIncentive } from "@omni/types/api/product/bff/shared/salesIncentive.model";
import { Media } from "@omni/types/api/shared/bff/media.model";
import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { ProductSliderProps } from "@omni/types/components/props/productSlider.props";
import { isDefined } from "@omni/utils/helper.utils";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { useAccessories } from "@/components/Product/Hooks/accessories.hook";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import { getProductDetail } from "@/redux/modules/value/productDetail/productDetail.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { GetStoreStrategy } from "@/services/store.service";
import { useStoreCode } from "@/utils/hooks/store/useStoreCode.hook";

import { AddToCartComponent } from "./addToCart.component";

export type AddToCartProps = {
    sku: string;
    title: string;
    medias: Array<Media> | null;
    salesIncentives?: Array<SalesIncentive>;
    visibilityType?: ProductVisibilityType;
    boxArticle: boolean;
    additionalService: boolean;
    strikethroughPrice?: number | null;
    currentPrice: number;
    promotions: Array<Promotion>;
    serverSideAccessoires?: Array<Product<ProductFieldSet.ProductTile>>;
    productSliderProps?: ProductSliderProps;
    onClose?: () => void;
};

export const AddToCart = ({
    sku,
    title,
    medias,
    salesIncentives,
    visibilityType,
    boxArticle,
    additionalService,
    strikethroughPrice,
    currentPrice,
    promotions,
    serverSideAccessoires,
    productSliderProps,
    onClose
}: AddToCartProps) => {
    const dispatch = useThunkDispatch();
    const { t } = useGranite();
    const { accessories: clientSideAccessoires, loadAccessories } =
        useAccessories();

    const { deliveryType } = useSelector(getProductDetail);

    const storeCode = useStoreCode({ strategy: GetStoreStrategy.Stock });

    const sliderDisabled = useMemo(() => {
        // Only render slider if required properties are defined
        if (!isDefined(productSliderProps)) return true;
        if (!isDefined(visibilityType)) return true;

        // AlgoliaRecommend slider requires extra validation logic based on product type and visibility,
        // other sliders can be rendered as long as the required properties are defined
        if (
            productSliderProps?.source !== ProductSliderSources.AlgoliaRecommend
        ) {
            return false;
        }

        if (boxArticle) return true;
        if (additionalService) return true;

        return [
            ProductVisibilityType.Offline,
            ProductVisibilityType.NotAvailable
        ].includes(visibilityType);
    }, [boxArticle, additionalService, productSliderProps, visibilityType]);

    const accessories = serverSideAccessoires ?? clientSideAccessoires;

    useEffect(() => {
        if (isDefined(accessories)) return;
        if (sliderDisabled) return;

        loadAccessories(sku);
    }, [sku, accessories, sliderDisabled, loadAccessories]);

    const handleOnAddAccessory = (
        sku: string,
        product: Product<ProductFieldSet.ProductTile>
    ) => {
        if (!deliveryType) return Promise.resolve();

        return dispatch(
            CartModule.addProductTileProductToCart(
                {
                    deliveryType,
                    quantity: 1,
                    shopId: storeCode,
                    sku: sku
                },
                product
            )
        )
            .then(() => {
                broadcastMessage({
                    id: "add-to-cart-modal-messages",
                    text: t("product.addtocart.modal.success.addAccessory"),
                    status: "success",
                    dismissible: true
                });
            })
            .catch(() => {
                broadcastMessage({
                    id: "add-to-cart-modal-messages",
                    text: t("product.addtocart.modal.failure.addAccessory"),
                    status: "danger",
                    dismissible: true
                });
            });
    };

    return (
        <AddToCartComponent
            title={title}
            medias={medias ?? []}
            strikethroughPrice={strikethroughPrice}
            currentPrice={currentPrice}
            isBoxArticle={boxArticle}
            promotions={promotions}
            salesIncentives={salesIncentives}
            accessories={accessories ?? []}
            productSliderProps={sliderDisabled ? undefined : productSliderProps}
            onAddAccessory={handleOnAddAccessory}
            onClose={onClose}
        />
    );
};
